import { useEffect, useState } from 'react';

export interface VisualViewportState {
  /**
   * Visible viewport height in CSS px, tracking `window.visualViewport`
   * (which shrinks when an on-screen keyboard is open). `null` on the
   * server and in browsers that don't implement the Visual Viewport API --
   * callers must fall back to a static CSS value (e.g. `90vh`) in that case.
   */
  height: number | null;
  /**
   * Distance in CSS px from the top of the layout viewport to the top of
   * the visual viewport. Non-zero when the browser has panned the visible
   * area (notably iOS Safari scrolling the focused input into view above
   * an open keyboard). `0` on the server, and in browsers without
   * `visualViewport` support (there's nothing to offset from in that case).
   */
  offsetTop: number;
  /**
   * `window.innerHeight` -- the full layout viewport height. Needed
   * alongside `height`/`offsetTop` to compute how far a bottom-anchored
   * element must shift to stay above an on-screen keyboard: on iOS Safari
   * the keyboard does NOT shrink the layout viewport (only `visualViewport`
   * shrinks and pans), so anything positioned relative to the layout
   * viewport's bottom edge stays glued behind the keyboard unless it reads
   * this value too. `null` on the server.
   */
  windowInnerHeight: number | null;
}

function read_visual_viewport_state(): VisualViewportState {
  if (typeof window === 'undefined') {
    return { height: null, offsetTop: 0, windowInnerHeight: null };
  }
  const vv = window.visualViewport;
  return {
    height: vv ? vv.height : null,
    offsetTop: vv ? vv.offsetTop : 0,
    windowInnerHeight: window.innerHeight,
  };
}

/**
 * Tracks `window.visualViewport`'s height/offsetTop (plus the layout
 * viewport's `innerHeight`) so overlay panels (Drawer's bottom-sheet
 * presentation) can both bound their max-height to the space actually
 * visible above an on-screen keyboard AND shift themselves up to stay
 * above it -- see `get_bottom_sheet_keyboard_offset` in
 * `packages/jumpstone-ui-react/src/components/drawer/drawer_layout.ts` for
 * why both `height` and `offsetTop` are required for that second part (a
 * max-height cap alone shrinks a bottom-anchored panel from the top while
 * its bottom edge stays glued behind the keyboard on iOS Safari, since iOS
 * does not shrink the layout viewport when the keyboard opens).
 *
 * SSR-safe: returns the zeroed/null state above when `window` doesn't
 * exist yet, and the same when the browser doesn't implement the Visual
 * Viewport API -- callers fall back to a static viewport-height unit in
 * that case.
 *
 * @param enabled Set to `false` to skip subscribing entirely (e.g. a
 * closed Drawer, or one using `side="end"` where this data is unused) --
 * avoids re-rendering every mounted consumer on every keyboard/pinch-zoom
 * event when its value isn't needed. Defaults to `true`.
 */
export function useVisualViewport(enabled = true): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>(
    read_visual_viewport_state,
  );

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    function handle() {
      setState(read_visual_viewport_state());
    }

    handle();
    window.visualViewport?.addEventListener('resize', handle);
    window.visualViewport?.addEventListener('scroll', handle);
    // `visualViewport`'s own `resize` event does not fire in every browser
    // for every layout-viewport size change (e.g. some Android rotations) --
    // window `resize` covers `windowInnerHeight` staying current too.
    window.addEventListener('resize', handle);
    return () => {
      window.visualViewport?.removeEventListener('resize', handle);
      window.visualViewport?.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
    };
  }, [enabled]);

  return state;
}
